import React, { useState, useEffect, useMemo } from 'react';
import { FaChevronLeft, FaChevronRight, FaExclamationTriangle } from 'react-icons/fa';

import BitaSpinner from '../../../../../../components/BitaSpinner/BitaSpinner';

import './IndexSlider.scss';

const ITEMS_PER_PAGE = 5;
const button_WIDTH = 120;
const button_GAP = 10;

const BASE_button_STYLE = {
  width: `${button_WIDTH}px`, // This will automatically update all buttons
  color: '#fff',
  fontWeight: 700,
  fontSize: '12px',
  lineHeight: '14.4px',
  letterSpacing: '1.37px',
  textAlign: 'center',
  padding: '8px 16px 8px 16px',
  borderRadius: '3.15px',
  cursor: 'pointer',
  height: '30px',
  outline: 'none',
  margin: 0,
  boxShadow: 'none',
};

const button_STYLES = {
  selected: {
    ...BASE_button_STYLE,
    background: 'linear-gradient(270deg, #2962FF, #0039CB)',
    border: 'none',
  },
  unselected: {
    ...BASE_button_STYLE,
    background: 'transparent',
    border: '1px solid #fff',
  },
};

const IndexSlider = ({ indexes, selectedIndex, setSelectedIndex, isLoading = false }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);

  const trackRef = React.useRef(null);
  const totalPages = Math.ceil(indexes.length / ITEMS_PER_PAGE);

  const sortedIndexes = useMemo(() => {
    return (indexes ?? [])
      .slice()
      .sort((a, b) => (a.symbol > b.symbol ? 1 : a.symbol < b.symbol ? -1 : 0));
  }, [indexes]);

  useEffect(() => {
    setShowLeftArrow(currentPage > 0);
    setShowRightArrow(currentPage < totalPages - 1);
  }, [currentPage, totalPages]);

  const handleMouseDown = e => {
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragOffset(0);
  };

  const handleMouseMove = e => {
    if (!isDragging) return;

    const currentX = e.clientX;
    const difference = dragStartX - currentX;
    setDragOffset(difference);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;

    const trackWidth = trackRef.current?.offsetWidth || 0;
    const moveThreshold = trackWidth * 0.2; // 20% threshold for page change

    if (Math.abs(dragOffset) > moveThreshold) {
      const direction = dragOffset > 0 ? 1 : -1;
      const nextPage = Math.max(0, Math.min(totalPages - 1, currentPage + direction));
      setCurrentPage(nextPage);
    }

    setIsDragging(false);
    setDragOffset(0);
  };

  const calculateTransform = () => {
    if (!isDragging) {
      const itemWidth = button_WIDTH + button_GAP;
      const offset = -(currentPage * ITEMS_PER_PAGE * itemWidth);
      return `translateX(${offset}px)`;
    }

    const baseTransform = -(currentPage * (button_WIDTH + button_GAP) * ITEMS_PER_PAGE);
    return `translateX(${baseTransform - dragOffset}px)`;
  };

  const scrollLeft = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const scrollRight = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getItemsForCurrentPage = () => {
    const startIndex = currentPage * ITEMS_PER_PAGE;
    const remainingItems = indexes.length - startIndex;
    return Math.min(remainingItems, ITEMS_PER_PAGE);
  };

  const visibleItems = getItemsForCurrentPage();
  const actualWidth = visibleItems * button_WIDTH + (visibleItems - 1) * button_GAP;

  return (
    <div className="index-button-slider-container">
      {isLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px',
          }}
        >
          <BitaSpinner />
        </div>
      ) : (
        <>
          {indexes.length > 1 && (
            <button
              className={`nav-arrow left ${!showLeftArrow ? 'hidden' : ''}`}
              onClick={scrollLeft}
            >
              <FaChevronLeft />
            </button>
          )}

          <div
            className="index-button-slider-track-container"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{
              cursor: isDragging ? 'grabbing' : 'grab',
              width: `${actualWidth}px`,
            }}
          >
            <div
              ref={trackRef}
              className="index-button-slider-track"
              style={{
                transform: calculateTransform(),
                transition: isDragging ? 'none' : 'transform 0.3s ease-out',
                display: 'flex',
                gap: `${button_GAP}px`,
              }}
            >
              {sortedIndexes.map(index => (
                <button
                  key={`${index.symbol}`}
                  className="index-slider-button"
                  onClick={() => setSelectedIndex(index.symbol)}
                  style={{
                    ...BASE_button_STYLE,
                    ...(index.symbol === selectedIndex
                      ? button_STYLES.selected
                      : button_STYLES.unselected),
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span>{index.symbol}</span>
                    {index.hasAnError && (
                      <span
                        style={{
                          marginLeft: 6,
                          color: '#FFC107',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <FaExclamationTriangle />
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {indexes.length > 1 && (
            <button
              className={`nav-arrow right ${!showRightArrow ? 'hidden' : ''}`}
              onClick={scrollRight}
            >
              <FaChevronRight />
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default IndexSlider;
